// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { Tooltip } from '@mui/material';
import { type EsgAnalysis } from '@shared/data-access/interfaces/api/ai/esg-analysis';
import { type HistoricalData } from '@shared/data-access/interfaces/api/commons';
import { type PortfolioAnalysis } from '@shared/data-access/interfaces/api/ai/portfolio-analysis';
import { type SavingHolding } from '@shared/data-access/interfaces/api/engine/citec';
import { BackButton } from '@advisor-app/components';
import { Tabs, useTabsController } from '@shared/ui-kit/components/tabs';
import { Backtest } from '@advisor-app/features/backtest/components';
import { Esg } from '@advisor-app/features/esg';
import { Overview } from '@shared/ui-kit/features/overview';
import {
  renderColumnsAnalysis,
  renderColumnsAnalysisExtended,
  renderColumnsTradesSummary,
  useGetPositions,
} from '@advisor-app/features/positions';
import { handleWorkflow } from '@advisor-app/features/positions/utils/handle-workflow';
import { Simulation } from '@advisor-app/features/simulation';
import { Risk } from '@advisor-app/features/risk';
import { Trades } from '@advisor-app/features/trades';
import { IconButton } from '@shared/ui-kit/components/icon-button';
import {
  MODAL_TEMPLATES_OPTIONS,
  showModal,
} from '@shared/ui-kit/components/popups/show-modal';
import { PageContainer } from '@shared/ui-kit/components/page-container';
import { PeriodPicker } from '@shared/ui-kit/components/period-picker';
import {
  exportTableToExcel,
  formatDate,
  numberToFixed,
} from '@shared/ui-kit/utils';
import { getKpisData } from '@advisor-app/features/utils/get-kpis-data';
import { savePortfolio } from '@advisor-app/features/utils/save-portfolio';
import { useAppNavigate } from '@advisor-app/hooks';
import {
  useSendToTrader,
  AddOperationsIconButton,
} from '@advisor-app/features/operations';
import { type FC, useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router';
import { AppNavigate } from '@advisor-app/routes';
import { useAppSelector } from '@advisor-app/store/store';
import {
  selectBenchmark,
  selectObjectiveKey,
} from '@advisor-app/store/slices/parameters';
import { selectWorkflowState } from '@advisor-app/store/slices/workflow';
import {
  portfolioPositionsSlice,
  selectCurrentPortfolio,
  selectTradesTable,
} from '@shared/data-access/slices/portfolio-positions';
import { selectIdeas } from '@advisor-app/store/slices/my-research';
import { selectAccountCurrency } from '@shared/data-access/slices/user';
import type { Paths } from '@shared/data-access/interfaces/routes/paths';
import { useTranslation } from 'react-i18next';
import type { KpiTranslationKey } from '@advisor-app/features/utils/get-kpis-data';
import type { ColumnMethods } from '@advisor-app/features/trades';

export const Positions: FC<{ backTo: Paths }> = ({ backTo }) => {
  const { t, i18n } = useTranslation(['common']);
  const navigate = useAppNavigate();
  const dispatch = useDispatch();

  const [params, setParams] = useSearchParams();

  const currency = useAppSelector(selectAccountCurrency);
  const [benchmark] = useAppSelector(state => selectBenchmark(state, 'ai-builder'));
  const workflow = useAppSelector(selectWorkflowState);
  const objective = useAppSelector(state =>
    selectObjectiveKey(state, 'ai-builder'),
  );

  const { asset: assetType, type: workflowType } = workflow;

  const holdings = useAppSelector(selectCurrentPortfolio);
  const positions = useAppSelector(selectTradesTable);

  const portfolios = useAppSelector(state =>
    selectIdeas(state).map(idea => idea.portfolio),
  );

  const {
    isLoadingAnalysis,
    isLoadingPositions,
    backtest,
    esgAnalysis,
    analysis,
    simulation,
    handleDownloadReport,
    assetsInfo,
    setHist,
    defaultHist,
    handleDeleteTradePosition,
  } = useGetPositions({
    holdings,
  });

  const isLoading = isLoadingAnalysis || isLoadingPositions;

  const tabsController = useTabsController(
    params.get('tab') ?? 'trades',
    undefined,
    tab => {
      setParams({ tab: tab });
    },
  );

  const { sendTaxPositions, sendPortfolioPositions } = useSendToTrader();

  const { start_date: start, end_date: end } =
    analysis?.historical_data ?? ({} as HistoricalData);

  const initialPeriod = formatDate(start, 'long', i18n.language);

  const finalPeriod = formatDate(end, 'long', i18n.language);

  const headerConfiguration = useMemo(
    () => ({
      trades: {
        title: t('common.trades', { ns: 'common' }),
      },
      overview: {
        title: t('common.overview', { ns: 'common' }),
        dates: `${initialPeriod} - ${finalPeriod}`,
      },
      risk: {
        title: t('common.risk', { ns: 'common' }),
        dates: `${initialPeriod} - ${finalPeriod}`,
      },
      backtest: {
        title: t('common.performance-backtest', { ns: 'common' }),
        dates: `${initialPeriod} - ${finalPeriod}`,
      },
      esg: {
        title: t('common.esg', { ns: 'common' }),
        subtitle: !esgAnalysis?.metrics
          ? t('messages.esg-not-available', { ns: 'components' })
          : undefined,
        dates: `${initialPeriod} - ${finalPeriod}`,
      },
      simulation: {
        title: t('common.simulation', { ns: 'common' }),
        dates: `${initialPeriod} - ${finalPeriod}`,
      },
    }),
    [initialPeriod, finalPeriod, t, esgAnalysis?.metrics],
  );

  const savingHoldings: SavingHolding[] = useMemo(
    () =>
      positions.map(item => ({
        ticker: item.ticker,
        outstanding_balance: item.outstanding_balance,
        unrealized_capital_gains: item.unrealized_capital_gains,
        weight: item.weight / 100,
      })),
    [positions],
  );

  const dataForTable = useMemo(
    () =>
      positions
        .map((position, index) => {
          const holding = holdings.find(h => h.ticker === position.ticker);
          return {
            id: index,
            name: position.name,
            weight: position.weight,
            quantity: holding?.qty || 1,
            price: holding?.price ? holding?.price : position.price ?? 1,
            outstanding_balance: position.outstanding_balance,
            unrealized_capital_gains: [
              position.unrealized_capital_gains,
              numberToFixed(
                (position.unrealized_capital_gains / position.outstanding_balance) *
                  100,
                2,
              ),
            ],
            url_logo: position.url_logo,
            url_morningstar: position.url_morningstar,
            asset_type: assetType,
            operation: position.operation,
            ticker: position.ticker,
            isin: position.isin,
            volume: position.volume,
            ytd_return: position.ytd_return,
            y1_return: position.y1_return,
            y3_return: position.y3_return,
            y5_return: position.y5_return,
            y10_return: position.y10_return,
            y3_vol: position.y3_vol,
            max_draw_down: position.max_draw_down,
            asset_class: position.asset_class,
            inception_return: position?.inception_return,
            m1_return: position?.m1_return,
            m3_return: position?.m3_return,
            m6_return: position?.m6_return,
            price_as_of: position?.price_as_of,
          };
        })
        .sort((a, b) => a.weight - b.weight),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [assetType, holdings, positions],
  );

  const handleDownloadTrades = useCallback(() => {
    const formattedData = dataForTable.map(
      ({ unrealized_capital_gains, ...rest }) => ({
        ...rest,
        unrealized_capital_gains: unrealized_capital_gains[0],
      }),
    );

    exportTableToExcel(formattedData, 'trades');
  }, [dataForTable]);

  const hasOnlyOneData = useMemo(() => dataForTable.length === 1, [dataForTable]);

  const handleDeleteTradeSummary = (ticker: string) => {
    const index = positions.findIndex(p => p.ticker === ticker);

    if (index !== -1) {
      dispatch(portfolioPositionsSlice.actions.deleteTradesTablePosition(index));
    }
  };

  const handleSavePortfolio = useCallback(() => {
    savePortfolio({
      benchmark: benchmark.ticker,
      savingHoldings,
      portfolios,
      t,
    });
  }, [benchmark.ticker, portfolios, savingHoldings, t]);

  const handleTaxHarvesting = () => {
    handleWorkflow(
      workflowType,
      holdings,
      positions,
      navigate,
      `/tax-manager/${assetType}` as Paths,
    );
  };

  const handleRebalance = () => {
    handleWorkflow(workflowType, holdings, positions, navigate, '/rebalance');
  };

  const handleSendToTrader = async () => {
    try {
      await (workflow.type === 'tax-harvesting'
        ? sendTaxPositions()
        : sendPortfolioPositions());

      navigate('/operations');
    } catch (error) {
      console.error('[Positions]: handleSendToTrader, ', error);
    }
  };
  const kpiData = useMemo(() => {
    return getKpisData(positions, holdings, workflow, analysis).map(kpi => ({
      ...kpi,
      text: t(kpi.text as KpiTranslationKey),
    }));
  }, [positions, holdings, workflow, analysis, t]);

  useEffect(() => {
    if (
      (!backtest || !simulation || !analysis || !esgAnalysis) &&
      tabsController.tab !== 'trades'
    ) {
      tabsController.setTab('trades');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isLoadOrAssetAllocation =
    workflowType === 'load' ||
    workflowType === 'asset-allocation' ||
    workflowType === 'research';
  const allHoldingsHavePriceOne = holdings.every(holding => holding.price === 1);

  let tableColumns =
    isLoadOrAssetAllocation && allHoldingsHavePriceOne
      ? renderColumnsAnalysis
      : renderColumnsAnalysisExtended;

  if (!isLoadOrAssetAllocation) {
    tableColumns = (columnMethods: ColumnMethods) =>
      renderColumnsTradesSummary(columnMethods, t);
  }

  if (workflowType !== 'load') {
    const isAiBuilderSelected = !!benchmark && !!objective;
    if (!isAiBuilderSelected) {
      showModal({
        ...MODAL_TEMPLATES_OPTIONS(t).UNHANDLED_ERROR,
        title: t('modals.oops-title', { ns: 'components' }),
        content: t('messages.missing-benchmark-objective', {
          ns: 'components',
        }),
        buttons: {},
      });
      return <AppNavigate to="/ai-builder" />;
    }
  }

  return (
    <PageContainer>
      <Tabs.Root controller={tabsController} defaultValue="trades">
        <div className="mb-2 flex items-center gap-4">
          <Tabs.Scrollable className="min-w-0 flex-1">
            <Tabs.List className="w-max">
              <Tabs.Trigger value="trades">
                {t('common.trades', { ns: 'common' })}
              </Tabs.Trigger>
              <Tabs.Trigger value="overview" disabled={!analysis}>
                {t('common.overview', { ns: 'common' })}
              </Tabs.Trigger>

              <Tabs.Trigger value="risk" disabled={!analysis}>
                {t('common.risk', { ns: 'common' })}
              </Tabs.Trigger>

              {!!backtest && (
                <Tabs.Trigger value="backtest">
                  {t('common.backtest', { ns: 'common' })}
                </Tabs.Trigger>
              )}
              {esgAnalysis?.metrics &&
              !['etf', 'mutual_fund', 'crypto'].includes(assetType) ? (
                <Tabs.Trigger value="esg">
                  {t('common.esg', { ns: 'common' })}
                </Tabs.Trigger>
              ) : null}

              <Tabs.Trigger value="simulation" disabled={!simulation}>
                {t('common.simulation', { ns: 'common' })}
              </Tabs.Trigger>
            </Tabs.List>
          </Tabs.Scrollable>

          <BackButton data-testid="back" to={backTo} />
        </div>

        <div
          data-testid="tab-header"
          className="mb-6 mt-12 flex items-start justify-between gap-4"
        >
          {Array.from(Object.entries(headerConfiguration), ([tab, options]) => (
            <Tabs.Slot value={tab} key={tab}>
              <div className="flex flex-col">
                <Tabs.Slot
                  value={['overview', 'risk', 'backtest', 'esg', 'simulation']}
                >
                  {'dates' in options && (
                    <h5
                      className="!mt-5 ml-2 font-normal italic text-graph-text"
                      data-testid="hist-dates"
                    >
                      {options?.dates}
                    </h5>
                  )}
                  <div className="mt-2">
                    <PeriodPicker
                      defaultValue={defaultHist}
                      onValueChange={setHist}
                    />
                  </div>
                </Tabs.Slot>
              </div>
            </Tabs.Slot>
          ))}

          <div className="flex flex-wrap gap-4 self-end">
            <AddOperationsIconButton
              onClick={handleSendToTrader}
              data-testid="test-execute-trades"
            />

            <Tooltip title={t('common.analysis-reports', { ns: 'common' })}>
              <IconButton
                icon="Reporting"
                onClick={handleDownloadReport}
                data-testid="test-reports"
              />
            </Tooltip>

            {!(
              workflowType === 'tax-harvesting' || assetType === 'mutual_fund'
            ) && (
              <Tooltip title={t('common.tax-harvesting', { ns: 'common' })}>
                <IconButton
                  icon="TaxManager"
                  onClick={handleTaxHarvesting}
                  data-testid="tax-harvesting-test-button"
                />
              </Tooltip>
            )}
            {workflowType !== 'tax-harvesting' && (
              <Tooltip title={t('common.rebalance', { ns: 'common' })}>
                <IconButton
                  icon="Rebalance"
                  onClick={handleRebalance}
                  data-testid="rebalance-test-button"
                />
              </Tooltip>
            )}
            <Tooltip title={t('common.save-portfolio', { ns: 'common' })}>
              <IconButton
                icon="Save"
                onClick={handleSavePortfolio}
                data-testid="save"
              />
            </Tooltip>

            <Tooltip title={t('common.download-trades', { ns: 'common' })}>
              <IconButton
                icon="Download"
                onClick={handleDownloadTrades}
                data-testid="trades-download-button"
              />
            </Tooltip>
          </div>
        </div>

        <Tabs.Content value="trades">
          <Trades
            tableColumns={tableColumns}
            columnMethods={{
              tickerMethod: handleDeleteTradeSummary,
              handleDeleteTrade: handleDeleteTradePosition,
              assetType: assetType,
              currency: currency,
              hasOnlyOneData: hasOnlyOneData,
            }}
            tableData={dataForTable}
            kpisDataArray={kpiData}
            handleSendToTrader={handleSendToTrader}
            handleTaxHarvesting={handleTaxHarvesting}
            handleRebalance={handleRebalance}
            handleSavePortfolio={handleSavePortfolio}
            handleDownloadReport={handleDownloadReport}
            excelFileName="trades"
            showSave={false}
            showSendToTrader={false}
            showTaxHarvesting={false}
            showRebalance={false}
            showDownloadReport={false}
            showDownload={false}
            showDeleteAllTrades={false}
            showExecuteTrades={false}
            showFormAddTicker={false}
            state-id="trades-positions-table"
          />
        </Tabs.Content>

        {!!analysis && (
          <Tabs.Content value="overview">
            <Overview
              analysis={analysis as PortfolioAnalysis}
              assetType={assetType}
              currency="USD"
            />
          </Tabs.Content>
        )}
        {!!analysis && (
          <Tabs.Content value="risk">
            <Risk
              assetType={assetType}
              analysis={analysis as PortfolioAnalysis}
              assetsInfo={assetsInfo || []}
            />
          </Tabs.Content>
        )}

        {!!backtest && (
          <Tabs.Content value="backtest">
            <Backtest
              analysis={analysis as PortfolioAnalysis}
              backtest={backtest}
              loading={isLoading}
            />
          </Tabs.Content>
        )}

        {esgAnalysis?.metrics && (
          <Tabs.Content value="esg">
            <Esg esgAnalysis={esgAnalysis as EsgAnalysis} assetsInfo={assetsInfo} />
          </Tabs.Content>
        )}

        {!!analysis && (
          <Tabs.Content value="simulation">
            <Simulation
              analysis={analysis as PortfolioAnalysis}
              defaultSimulation={simulation!}
            />
          </Tabs.Content>
        )}
      </Tabs.Root>
    </PageContainer>
  );
};

export default Positions;
